import dayjs, { Dayjs } from 'dayjs'
import { ScheduledTime } from '../redux/emailTemplateService'
import { RangeFilters } from '../redux/corporateCodeService'
import Cookies from 'universal-cookie';
import { BrokerSourceStatsInitialState, BrokerSourceType, FamilyUserTypeFilterType, RemovalStatsType } from '../components/Views/UserReports/Types';
const cookies = new Cookies();

export type LastSyncItem = Record<string, string>;

export enum Environments {
  local = 'local',
  sandbox = 'sandbox',
  development = 'development',
  staging = 'staging',
  production = 'production',
}
export type LogLevels = 'DEBUG' | 'INFO' | 'ERROR'
export const currentEnvironment = (): Environments => {
  if (process.env.REACT_APP_LOCAL) {
    return Environments.local
  } else if ((process.env.REACT_APP_API_FQDN as string).includes('sandbox')) {
    return Environments.sandbox
  } else if (
    (process.env.REACT_APP_API_FQDN as string).includes('development')
  ) {
    return Environments.development
  } else if ((process.env.REACT_APP_API_FQDN as string).includes('staging')) {
    return Environments.staging
  }

  return Environments.production
}

export const logLevel = (loggerLevel: LogLevels): LogLevels => {
  if (currentEnvironment() === Environments.local) {
    return 'DEBUG'
  } else {
    return loggerLevel
  }
}

export const bytesToGbConverter = (size: number): number => {
  return size / (1000 * 1000 * 1000)
}

export const compositeCMSString = (
  cmsString: string,
  replacement: string | string[],
  replacing = '{0}',
): string => {
  try {
    if (Array.isArray(replacement)) {
      let replacedString = cmsString
      replacement.forEach((replace, index) => {
        replacedString = replacedString.replace(`{${index}}`, replace)
      })
      return replacedString
    } else {
      return cmsString.replace(replacing, replacement)
    }
  } catch (error) {
    // fail silently. don't worry it's safe
    return ''
  }
}

export const isObjectEmpty = (obj: any): boolean =>
  obj && Object.keys(obj).length === 0

export const isValidDate = (d: any): boolean => {
  return d instanceof Date && !isNaN(d.getTime())
}

export const formatDate = (date: string): string => {
  const newDate = new Date(date)

  return isValidDate(newDate)
    ? `${newDate.getMonth() + 1}/${newDate.getDate()}/${newDate.getFullYear()}`
    : ''
}

export const formatToDateOnly = (date: Date): string => {
  const newDate = new Date(date)

  return isValidDate(newDate)
    ? `${newDate.getFullYear()}-${newDate.getMonth() + 1}-${newDate.getDate()}`
    : ''
}

// zips 2 arrays in order - if the lengths are different, returns an array of the larger length with missing elements
// returned as undefined
export const zip = <T, U>(a1: T[], a2: U[]): [T?, U?][] => {
  if (a2.length > a1.length) {
    return a2.map((a, i) => [a1[i], a])
  } else {
    return a1.map((a, i) => [a, a2[i]])
  }
}

// from https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html
export const assertNever = (x: never): never => {
  throw new Error('Unexpected object: ' + x)
}

export const formatDayDate = (date: Dayjs) => {
  return date.format('YYYY-MM-DD')
}

export const formatBrokerNames = (brokerNames: any) => {
  return (brokerNames = brokerNames
    .flatMap((brokerName: string) => {
      const updatedBrokerName = brokerName.split('.')[0] // Remove the domain from the broker name
      return [updatedBrokerName, brokerName]
    }))
}



type ScheduledTimeType = string | Date;

interface ScheduledTimeObject {
  type: "end_of_month" | "relative_date";
  month: string;
  day: number;
  year: null | number;
}

export const prepareScheduledTime = (
  scheduledTime: ScheduledTimeType,
  scheduledMonth?: Date
): ScheduledTimeObject => {
  if (scheduledTime === "end_of_month" && scheduledMonth) {
    const monthName = dayjs(scheduledMonth).format("MMMM"); // Get month name
    const lastDay = dayjs(scheduledMonth).endOf("month").date(); // Get last day of the month

    return {
      type: "end_of_month",
      month: monthName,
      day: lastDay,
      year: null,
    };
  }

  if (dayjs(scheduledTime).isValid()) {
    const monthName = dayjs(scheduledTime).format("MMMM"); // Get month name
    const day = dayjs(scheduledTime).date();
    const year = dayjs(scheduledTime).year();

    return {
      type: "relative_date",
      month: monthName,
      day: day,
      year: year,
    };
  }

  throw new Error("Invalid scheduledTime or scheduledMonth arguments");
};


export const formatScheduledTime = (scheduledTime: ScheduledTime): string => {
  const { type, day, month, year } = scheduledTime;

  if (type === "end_of_month") {
    return month
  }

  if (type === "relative_date" && year) {
    // Construct the date from the provided day, month, and year
    const date = dayjs(`${month} ${day}, ${year}`);
    return date.format("YYYY-MM-DD");
  }

  return "Invalid Date";
};

export const getFilters = (filters: Record<string, any>) => {
  const updated_filters: Record<string, any> = {};

  Object.entries(filters).forEach(([key, value]) => {
    const isValid =
      (Array.isArray(value) && value.length > 0) ||
      (typeof value === 'boolean') ||
      (typeof value === 'string' && value.trim() !== '') ||
      (typeof value === 'number');

    if (isValid) {
      updated_filters[key] = value;
    }
  });

  return updated_filters;
};


export const getRangeFilters = (filters: Record<string, any>[]): RangeFilters[] => {
  let updated_filters: RangeFilters[] = [];
  filters.forEach((value: any) => {
    if (value?.rangeFilter && value?.startDate && value?.endDate) {
      updated_filters.push({
        key: value.rangeFilter.toLowerCase().replace(' ', '_'),
        first_value: formatToReportDateString(value.startDate),
        second_value: formatToReportDateString(value.endDate),
      });
    }
  });
  return updated_filters;
};

export const getNumberRangeFilters = (filters: Record<string, any>[]): RangeFilters[]=>{
  let updated_filters: RangeFilters[] = [];
  
  filters.forEach((value: any) => {
    if (value?.key && (value?.endValue || value?.endValue===0)) {
      updated_filters.push({
        key: value.key,
        first_value: value.startValue,
        second_value: value.endValue,
      });
    }
  });

  return updated_filters;
}


export const formatToReportDateString = (date: Dayjs): string => {
  return date.format('YYYY-MM-DD HH:mm:ss.SSS');
};

export const formatFromReportDateString = (date: string): string => {
  return new Date(date).toISOString().split('T')[0];
};


export const getSubStringFilters = (value: string, familySearch: FamilyUserTypeFilterType | null = null) => {
  const trimmedValue = value.trim();
  if (trimmedValue) {
    if (validateEmail(trimmedValue)) {
      const key = familySearch? `${familySearch.toLowerCase()}_email`: 'email'
      return { [key]: trimmedValue };
    }
    const key = familySearch? `${familySearch.toLowerCase()}_muid`: 'muid'
    return { [key]: trimmedValue };
  }
  return {};
};


export const getSearchFilters = (value: any[]) => {
  let searchFilters: Record<string, any> = {};

  value.forEach((val: any) => {
    if (typeof val === 'string') {
      const filter = getSubStringFilters(val);
      searchFilters = { ...searchFilters, ...filter };
    } else if (Array.isArray(val) && val.length > 0) {
      const brokers = val.map((arrayVal: string) =>
        arrayVal.split('.')[0].trim()
      );
      searchFilters['broker'] = brokers;
    }
  });

  return searchFilters;
};

export const validateEmail = (email: string) => {
  if (!email) {
    return false
  }
  // this beautiful construction is a classic from
  // https://stackoverflow.com/questions/201323/how-can-i-validate-an-email-address-using-a-regular-expression
  return !!email.toLowerCase().match(
    // eslint-disable-next-line no-control-regex
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
  )
}

export const formatLastSyncTime = (Time: Record<string, string>[]) => {
  const formattedSyncTime: Record<string, string> = {};

  Time.forEach((entry) => {
    const [key, isoString] = Object.entries(entry)[0];
    
     if (!isoString || isNaN(new Date(isoString).getTime())) {
      formattedSyncTime[key] = "-";
      return;
    }

    const localTime = new Date(isoString);
    const now = new Date();

    const diffInMs = now.getTime() - localTime.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    let humanReadable = "";
    if (diffInMinutes < 1) {
      humanReadable = "just now";
    } else if (diffInMinutes < 60) {
      humanReadable = `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
    } else if (diffInHours < 24) {
      humanReadable = `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    } else {
      humanReadable = `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
    }

    formattedSyncTime[key] = humanReadable;
  });

  return formattedSyncTime;
};

const COOKIE_KEY = 'looker_last_sync';
const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

export const shouldRefetch = (data: LastSyncItem[], thresholdMs = FOUR_HOURS_MS) => {
  if (!Array.isArray(data)) return true;

  return data.some((item) => {
    const isoString = Object.values(item)[0];
    if (!isoString) return true;
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return true;
    const diff = Date.now() - d.getTime();
    return diff > thresholdMs;
  });
};

export const getCachedLastSync = (): LastSyncItem[] | null => {
  try {
    const raw = cookies.get(COOKIE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const setCachedLastSync = (data: LastSyncItem[]) => {
  cookies.set(COOKIE_KEY, JSON.stringify(data), {
    path: '/',
    maxAge: 60 * 60 * 24
  });
};

export const formatDataArray=(dataString: string)=>{
  const array = dataString
  .slice(1, -1)
  .split(",")
  .map(item => item.trim());
  return array.join(',')
}

export const formatAddressesArray=(addresses: string)=>{
  try{
    const addresses_array = JSON.parse(addresses);
      let unifiedAddresses = ''
      if((Array.isArray(addresses_array) && addresses_array.length !==0)){
        unifiedAddresses = addresses_array.map((addr: Record<string,any>) => {
          const parts = [addr.street1, addr.city, addr.state, addr.country];
          return parts.filter(Boolean).join(', ');
        }).join(' ; ')
      }
  return unifiedAddresses
  }
  catch(e){
    console.error('error parsing address array : ',e)
    return ''
  }
  
}

export const formatRemovalStatsString=(stats: string ): RemovalStatsType=>{
  try{
    return JSON.parse(stats);
  }
  catch(e){
    console.error('error parsing stats : ',e)
    return BrokerSourceStatsInitialState	
  }
}

export const formatRemovalStats=(stats:string | RemovalStatsType, brokerSource: BrokerSourceType[])=>{
  const formattedStats = typeof stats === 'string' ? formatRemovalStatsString(stats) : stats
  
  if(brokerSource.length===0){
    return formattedStats['total'].toString()
  }

  const values = brokerSource.map(k => formattedStats[k]);

  const accumulattedStats = values.reduce((total, currentValue)=>total+currentValue,0)
  return accumulattedStats 
 
}

export function normalizeCity(value: string): string {
  if (!value) return "";

  let normalized = value.replace(/_/g, " ");
  normalized = normalized
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return normalized;
}

export const getSubscriptionStatusStyle = (status: string | null) => {
    return {
        backgroundColor: !status ? '#fff' : status === 'Active' ? '#46c563' : status === 'Expired' ? '#ff051eff' : status === 'Failed' ? '#b00020' : status === 'Free' ? '#fdd355' : '#fff',
        color: status === 'Expired' || status === 'Failed' ? '#fff' : 'inherit'
    }
}

export const getInviteStatusStyle = (status: string | null) => {
    return {
        backgroundColor: !status ? '#fff' : status === 'Accepted' ? '#46c563' : status === 'Redeemed' ? '#2196f3' : (status === 'Failed' || status === 'Deleted' || status === 'Expired') ? '#ff051eff' : status === 'Invited' ? '#fdd355' : status === 'Inactive' ? '#e0e0e0' : '#fff',
        color: (status === 'Failed' || status === 'Deleted' || status === 'Expired' || status === 'Redeemed') ? '#fff' : 'inherit'
    }
}

export const getBillingStatusStyle = (status: string | null) => {
    return {
        backgroundColor: !status ? '#fff' : status === 'Paid' ? '#46c563' : status === 'Expired' ? '#ff051eff' : status === 'Unpaid' ? '#fdd355' : status === 'Inactive' ? '#e0e0e0' : '#fff',
        color: status === 'Expired' ? '#fff' : 'inherit'
    }
}